/**
 * ProcessingPill — Inline header indicator for active processing jobs.
 *
 * Lives in AppHeader, always visible regardless of Activity Bar state.
 * Supports expand/reduce toggle and multi-job carousel.
 *
 * Reduced: Compact pill with pulsing dot + job count (e.g., "● 2 processing")
 * Expanded: Full detail — "Processing Recording for John Doe · 45%" + carousel nav
 *
 * Phase 12: Non-blocking processing — replaces ActivityBarHeader processing zone.
 *
 * See: docs/features/infrastructure/backend-processing.md
 */
import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Minus } from "lucide-react";
import Tooltip from "@mui/material/Tooltip";
import "../../css/ProcessingPill.css";

const AUTO_CYCLE_INTERVAL = 4000; // ms between auto-cycling jobs

const ProcessingPill = ({ jobs = [] }) => {
    const [expanded, setExpanded] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [userInteracted, setUserInteracted] = useState(false);
    const autoCycleRef = useRef(null);

    const jobCount = jobs.length;

    // Clamp activeIndex when jobs change
    useEffect(() => {
        if (activeIndex >= jobCount) {
            setActiveIndex(Math.max(0, jobCount - 1));
        }
    }, [jobCount, activeIndex]);

    // Auto-cycle through jobs when expanded and user hasn't interacted
    useEffect(() => {
        if (!expanded || userInteracted || jobCount <= 1) {
            clearInterval(autoCycleRef.current);
            return;
        }

        autoCycleRef.current = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % jobCount);
        }, AUTO_CYCLE_INTERVAL);

        return () => clearInterval(autoCycleRef.current);
    }, [expanded, userInteracted, jobCount]);

    // Reset user interaction flag after 10s of no interaction
    useEffect(() => {
        if (!userInteracted) return;
        const timer = setTimeout(() => setUserInteracted(false), 10000);
        return () => clearTimeout(timer);
    }, [userInteracted]);

    if (jobCount === 0) return null;

    const activeJob = jobs[activeIndex] || jobs[0];

    const handlePrev = (e) => {
        e.stopPropagation();
        setUserInteracted(true);
        setActiveIndex((prev) => (prev - 1 + jobCount) % jobCount);
    };

    const handleNext = (e) => {
        e.stopPropagation();
        setUserInteracted(true);
        setActiveIndex((prev) => (prev + 1) % jobCount);
    };

    const handleReduce = (e) => {
        e.stopPropagation();
        setExpanded(false);
    };

    const handleExpand = () => {
        setExpanded(true);
    };

    // Build display text
    const isPending = activeJob?.isPending;
    const sourceLabel = activeJob?.sourceType || "Recording";
    const patientLabel = activeJob?.patientName || "Unknown";
    const statusLabel = activeJob?.statusLabel;
    const displayText = isPending
        ? `Submitting ${sourceLabel}…`
        : statusLabel
            ? `${statusLabel} for ${patientLabel}`
            : `Processing ${sourceLabel} for ${patientLabel}`;
    const progressLabel = isPending
        ? "Submitting…"
        : activeJob?.progressPercent > 0
            ? `${activeJob.progressPercent}%`
            : "Starting…";

    // Reduced pill — show statusLabel if available
    const reducedLabel = isPending
        ? `Submitting ${sourceLabel}…`
        : statusLabel
            ? statusLabel
            : jobCount === 1
                ? `Processing ${sourceLabel}…`
                : `${jobCount} processing`;

    // Reduced pill
    if (!expanded) {
        return (
            <Tooltip title="Click to expand processing details" arrow>
                <button
                    className="processing-pill processing-pill-reduced"
                    onClick={handleExpand}
                    aria-label={`${jobCount} job${jobCount !== 1 ? "s" : ""} processing`}
                >
                    <span className={`processing-pill-dot${isPending ? " pending" : ""}`} />
                    <span className="processing-pill-count">
                        {reducedLabel}
                    </span>
                </button>
            </Tooltip>
        );
    }

    // Expanded pill
    return (
        <div className="processing-pill processing-pill-expanded">
            {/* Carousel nav (only when multiple jobs) */}
            {jobCount > 1 && (
                <button
                    className="processing-pill-nav"
                    onClick={handlePrev}
                    aria-label="Previous job"
                >
                    <ChevronLeft size={12} />
                </button>
            )}

            {/* Job detail */}
            <div className="processing-pill-detail">
                <span className={`processing-pill-dot${isPending ? " pending" : ""}`} />
                <span className="processing-pill-text">
                    {displayText}
                </span>
                <span className="processing-pill-progress">
                    {progressLabel}
                </span>
                {jobCount > 1 && (
                    <span className="processing-pill-index">
                        {activeIndex + 1}/{jobCount}
                    </span>
                )}
            </div>

            {/* Carousel nav (only when multiple jobs) */}
            {jobCount > 1 && (
                <button
                    className="processing-pill-nav"
                    onClick={handleNext}
                    aria-label="Next job"
                >
                    <ChevronRight size={12} />
                </button>
            )}

            {/* Minimize button */}
            <Tooltip title="Minimize" arrow>
                <button
                    className="processing-pill-action"
                    onClick={handleReduce}
                    aria-label="Minimize processing pill"
                >
                    <Minus size={12} />
                </button>
            </Tooltip>
        </div>
    );
};

export default ProcessingPill;
